// BCM-898 · Goods Receipt Dashboard — API layer (all endpoints in one place).
//
// Reachability (API Reference §Routing & reachability): the frontend URL
//   {appUrl}/{appName}/{network}/{collabType}/{roleName}/<endpoint>
// puts the literal "workflow" into the CMS router's :userRole slot — it is a
// ROLE NAME, not a router prefix. An endpoint is browser-reachable only if it is
// registered on CMSRouter/CMORouter. Endpoints marked NOT-REACHABLE below sit on
// the workflow router only (D2a/D2b) — verify against the deployed env before
// building on them; the calls here fail gracefully so the UI degrades, not breaks.
//
// Response envelopes are NOT interchangeable:
//   { status, data } — reads (A1, A2, A3, A5, A6, A15)
//   { status }       — action writes (A8, A14, A14b, A16)
//   { success }      — generation (A9/A10) and Sync Inventory (A13)
import axios from "axios";
import { appUrl } from "../../../../../../utils/config";

const seg = () => {
  const { appName, network, collabType, roleName } = localStorage;
  return { appName, network, collabType, roleName };
};

// {collabType}/{roleName}/<ep> — the reachable shape used by the vast majority.
export const roleUrl = (ep) => {
  const { appName, network, collabType, roleName } = seg();
  return `${appUrl}/${appName}/${network}/${collabType}/${roleName}/${ep}`;
};
// {collabType}/workflow/<ep> — "workflow" is a roleName value (30 call sites).
// Reachable ONLY when the endpoint is registered on the role router too.
export const workflowUrl = (ep) => {
  const { appName, network, collabType } = seg();
  return `${appUrl}/${appName}/${network}/${collabType}/workflow/${ep}`;
};
// {collabType}/{vendorName}/{roleName}/<ep> — 5 segments, not roleUrl's 4. The
// CMO/vendor name (row.vendorName) sits between collabType and roleName. Only
// resetWorkflowFromScratch is confirmed to need this shape (BCM-898 Sync
// PO/Inventory 2-step chain) — do not reuse for endpoints that haven't been
// confirmed against this route.
export const resetWorkflowUrl = (ep, vendorName) => {
  const { appName, network, collabType, roleName } = seg();
  return `${appUrl}/${appName}/${network}/${collabType}/${roleName}/${ep}`;
};

// ── Section 2 — batch list ────────────────────────────────────────────────
// A1 · fetchBatchesDirect. Kept in LOCAL dashboard state (never the shared sasl
// Redux slice) to keep Batch Execution zero-regression. Caller supplies a
// CancelToken source (see useGoodsReceiptBatches).
export const fetchBatchesDirect = (body, cancelToken) =>
  axios.post(roleUrl("fetchBatchesDirect"), body, { cancelToken });

// ── Section 3 — batch detail + progress bar ──────────────────────────────
// A2 · fetchDeliveryScheduleBatchDetails (Tier 3 of the gate + progress bar).
export const fetchBatchDetails = (body, cancelToken) =>
  axios.post(roleUrl("fetchDeliveryScheduleBatchDetails"), body, { cancelToken });

// fetchAllFGBatches — full FG batch list for the Resubmit Document dialog's batch
// picker. The batch is pre-populated with the row it was launched from but stays
// editable (BCM-898 Resubmit Document redesign), so the dialog needs the same
// searchable list the Reset Batch dialog uses. Read-only GET — never touches the
// shared sasl Redux slice, so Batch Execution stays zero-regression.
export const fetchAllFGBatches = (cancelToken) =>
  axios.get(roleUrl("fetchAllFGBatches/true"), { cancelToken });

// ── Section 4 — documents-pending cards (PA1 / PA7 / PA12) ────────────────
// A3 · fetchDocumentTypes (configured docs). NOT REACHABLE (D2a): the POST
// cmoDocumentTypesList version is workflow-router-only. Do NOT wire the reachable
// GET twin (different contract). Attempted here so the card degrades cleanly.
export const fetchDocumentTypes = (body, cancelToken) =>
  axios.post(workflowUrl("fetchDocumentTypes"), body, { cancelToken });
// A15 · fetchDocuments (received docs, categorised server-side). Reachable.
export const fetchDocuments = (body, cancelToken) =>
  axios.post(roleUrl("fetchDocuments"), body, { cancelToken });

// ── Section 6 — Review Consumption (PA3) ─────────────────────────────────
// A5 · fetchComponentAllocationForPOLineNewDirect/{schrockenSLID} (GET).
export const fetchPOLineAllocation = (schrockenSLID, cancelToken) =>
  axios.get(roleUrl(`fetchComponentAllocationForPOLineNewDirect/${schrockenSLID}`), {
    cancelToken,
  });

// ── Section 7/8 — variance + failure cards (PA4/PA6/PA8) ─────────────────
// A6 · fetchComponentAllocationPlannedAndActualNewDirect/{batchID}/{poQuantity} (GET).
export const fetchPlannedVsActual = (batchID, poQuantity, cancelToken) =>
  axios.get(
    roleUrl(
      `fetchComponentAllocationPlannedAndActualNewDirect/${batchID}/${poQuantity}`
    ),
    { cancelToken }
  );
// S5 · fetchAllReceivingPlantIDsAndStorageLocationIDsDirect/{vendorMosymphonyID}
// (GET) — PA8 check-4 master data (per-vendor set).
export const fetchReceivingPlantSLoc = (vendorMosymphonyID, cancelToken) =>
  axios.get(
    roleUrl(
      `fetchAllReceivingPlantIDsAndStorageLocationIDsDirect/${vendorMosymphonyID}`
    ),
    { cancelToken }
  );

// A20 · checkForConsumptionBatchExpiry — the SAME validation the Temporal workflow
// itself calls at this point in the process flow (Step 7), called here with the
// EXACT request shape the workflow's own activity sends — verified against the
// currently-live workflow code (checkExpiredConsumptionBatchesActivity.ts + its
// call site in processFlow1Workflow.ts), NOT the middleware team's simplified
// contract proposed on 2026-08-27 (that one is not deployed and was superseded by
// this "match the workflow exactly" approach — see PA4MaterialExpired.jsx for the
// full request/response shape).
// (A21 checkConsumptionMaterialMismatch is NOT wired: PA5 "Consumed material not
// matching PO BoM" derives its mismatch client-side from the A2 detail's
// bomConsumptionDetails vs bomComponentDetails instead.)
// workflow-router endpoint (see the reachability note at the top of this file) —
// callers MUST fall back to the existing A5/A6-derived approach if it fails.
// `skipNotification: true` is REQUIRED on every call from this dashboard: without
// it, a failing check sends a real Planner/CMO notification email on every call —
// fine for the Temporal workflow (which only calls it once per recheck interval),
// wrong for a UI screen that may re-render/refresh far more often (deck slide 41:
// "notification sent first time only, not for UI triggered syncs/checks"). The
// controller honors this flag (njm-corda-pcmp) by skipping the notification-prep
// block entirely — which is also why productConfig can be omitted from the
// request below: it's only read inside that skipped block.
export const checkConsumedBatchExpiry = (body, cancelToken) =>
  axios.post(
    workflowUrl("checkForConsumptionBatchExpiry"),
    { ...body, skipNotification: true },
    { cancelToken }
  );

// A14 · updateFGBatchForWorkflowReset — document resubmission request (the
// Resubmit Document dialog). Action write: responds on `status`.
export const resubmitDocuments = (body, cancelToken) =>
  axios.post(roleUrl("updateFGBatchForWorkflowReset"), body, { cancelToken });

// sendMasterDataUpdateNotification — PA8's "MARD Data Updated" button (check 4,
// product-plant-SLoc mismatch). No batch processing of its own — it just fires
// the confirmation email to the Collabrix support team so they update MARD in
// SAP. Action write: responds on `status`.
export const sendMasterDataUpdateNotification = (body, cancelToken) =>
  axios.post(roleUrl("sendMasterDataUpdateNotification"), body, { cancelToken });

// ── Section 11 — sync controls (Sync PO / Sync Inventory) ────────────────
// A13 · syncInventoryDataNew (Sync Inventory). Responds on `success`.
export const syncInventory = (body, cancelToken) =>
  axios.post(roleUrl("syncInventoryDataNew"), body, { cancelToken });
// A16 · resetWorkflowFromScratch — step 2 of the Sync Inventory chain, and of
// the Sync PO chain when it still goes through resetsBatch. Called after step
// 1 (updateFGBatchForWorkflowReset for PO / syncInventory for Inventory)
// succeeds. batchResetFor on the body must be "PO Sync" or "Inventory Sync"
// to match which sync ran. Uses resetWorkflowUrl (5 segments — see above),
// NOT roleUrl. Responds on `status`, unlike the two syncs above which respond
// on `success`.
export const resetWorkflowFromScratch = (vendorName, body, cancelToken) =>
  axios.post(resetWorkflowUrl("resetWorkflowFromScratch", vendorName), body, { cancelToken });
// A14b · updateFGBatchForWorkflowReset (Sync PO). Same endpoint as
// resubmitDocuments (A14) and the sasl/batchesDetails Phase-2-reset flow —
// Sync PO goes through this single call for step 1. Body is an ARRAY of
// reset requests (matches the existing callers above — see
// buildPOWorkflowResetPayload in useSyncWithRecheck.js). Responds on
// `status`, not `success`.
export const updateFGBatchForWorkflowReset = (body, cancelToken) =>
  axios.post(roleUrl("updateFGBatchForWorkflowReset"), body, { cancelToken });

// updateSASLDSDataNew (Sync PO for a batch at SASLBatchStatusID 50 — Post-
// Shipment Documents Uploaded). Same endpoint/payload PurchaseOrderTabTable.jsx's
// handleSyncPO uses (writes SA + SL + DS + BOM + component allocation, NOT just
// SA + SL like the older /updateSASLDataNew). Used ONLY for status 50 — every
// other status's Sync PO keeps going through updateFGBatchForWorkflowReset
// above, unchanged. Responds on `success`, not `status`.
export const updateSASLDSDataNew = (body, cancelToken) =>
  axios.post(roleUrl("updateSASLDSDataNew"), body, { cancelToken });

// A22 · fetchBatchStatusDirect — lightweight status poll used by
// useAwaitBatchUpdate after a batch-mutating action returns. Returns only
// { batchStatus, updatedAt } — small enough to poll every 2–8s without load
// concerns. Read-only, no side effects, no notifications. Read envelope
// ({ status, data }). See middleware controller fetchBatchStatusDirect.js.
export const fetchBatchWorkflowStatus = (
  schrockenDeliveryScheduleBatchID,
  cancelToken
) =>
  axios.get(
    roleUrl(`fetchBatchStatusDirect/${schrockenDeliveryScheduleBatchID}`),
    { cancelToken }
  );

// ── Section 12 — post-GRN workflow reset (Sync SAP QA Check Interface) ─────
// resetWorkflowPostGrn — the ONLY call fired by the Sync SAP QA Check Interface
// button on the QA release card (PA10 / PA11). It pulls the SAP QA decision and
// resets the post-GRN workflow. Request keys (contract):
//   { poNumber, sponsorBatchNumber, batchNumber, vendorMosymphonyID,
//     schrockenDeliveryScheduleBatchID, batchResetFor: "QA SAP Sync" }
// Action-write envelope — responds on `status` (true/false) with a message +
// workflowId/resetType. Reachable role-router endpoint.
export const resetWorkflowPostGrn = (body, cancelToken) =>
  axios.post(roleUrl("resetWorkflowPostGrn"), body, { cancelToken });
